<template>
  <div>
    <el-dialog v-dialogDrag top="5vh" custom-class="rtc-private-video-dialog" :title="title" width="360px"
      :visible.sync="showRoom" :close-on-click-modal="false" :close-on-press-escape="false" :before-close="onQuit">
      <div class="rtc-private-video">
        <div class="rtc-voice-box" v-loading="!isChating" element-loading-text="等待对方接听..."
          element-loading-background="rgba(0, 0, 0, 0.1)">
          <head-image class="friend-head-image" :id="friend.id" :size="200" :name="friend.nickName"
            :url="friend.headImage" :isShowUserInfo="false">
            <div class="rtc-voice-name">{{ friend.nickName }}</div>
          </head-image>
        </div>
        <div class="rtc-control-bar">
          <div :title="isChating ? '挂断' : '取消'" class="icon iconfont icon-phone-reject reject"
            :class="{ 'is-disabled': quitting }" style="color: red;" @click="onQuit()"></div>
        </div>
      </div>
    </el-dialog>
    <rtc-private-acceptor v-if="!isHost && isWaiting" ref="acceptor" :friend="friend" @accept="onAccept"
      @reject="onReject"></rtc-private-acceptor>
  </div>
</template>

<script>
import HeadImage from '../common/HeadImage.vue';
import RtcPrivateAcceptor from './RtcPrivateAcceptor.vue';
import AgoraVoiceClient from '@/api/agoraRtc';
import RtcPrivateApi from '@/api/rtcPrivateApi'

// 语音通话组件(已从自研 WebRTC P2P 迁移到声网 Agora, 与 App 端
// public/webrtc/call.html 的实现对齐)。视频通话已下线, 只保留语音。
export default {
  name: 'rtcPrivateVideo',
  components: {
    HeadImage,
    RtcPrivateAcceptor
  },
  data() {
    return {
      agora: new AgoraVoiceClient(), // 声网客户端封装
      API: new RtcPrivateApi(), // 信令 HTTP API
      audio: new Audio(), // 呼叫铃声
      showRoom: false,
      friend: {},
      isHost: false, // 是否发起人
      state: "CLOSE", // CLOSE:关闭  WAITING:等待呼叫或接听 CHATING:聊天中
      // 待入会凭据 {appId, channel, uid, token}。主叫在 call 成功后存这里
      // 等对方接听, 被叫在收到呼叫信令时存这里(接听时优先用 accept 接口返的那份)
      pendingJoin: null,
      // 本通电话的唯一标识, 就是声网频道名(服务端每次呼叫新生成, 带毫秒时间戳)。
      // 用来把上一通迟到的信令挡在门外。
      currentCallId: null,
      accepting: false, // 接听流程进行中(防按钮连点重复触发)
      joining: false, // 正在入会(防 102 信令与状态对齐重复触发)
      quitting: false, // 挂断/取消已点击(防重)
      videoTime: 0,
      videoTimer: null,
      heartbeatTimer: null,
      waitTimer: null,
    }
  },
  methods: {
    open(rtcInfo) {
      this.showRoom = true;
      this.isHost = rtcInfo.isHost;
      this.friend = rtcInfo.friend;
      if (this.isHost) {
        this.onCall();
      }
    },
    initAudio() {
      let url = require(`@/assets/audio/call.wav`);
      this.audio.src = url;
      this.audio.loop = true;
    },
    // 建声网客户端并挂上事件回调。每通电话都重建(close 时已销毁)。
    initAgora() {
      this.agora.init();
      this.agora.onRemoteUserJoined = () => {
        console.log('[RTC] 对方音频已接通');
      };
      // 对方离开频道的兜底: 对方进程被杀/断网时只有频道事件, 没有 106 信令
      this.agora.onRemoteUserLeft = () => {
        if (this.isChating) {
          this.API.handup(this.friend.id).catch(() => {});
          this.$message.success("通话已结束");
          this.close();
        }
      };
      this.agora.onDisconnected = () => {
        if (this.isChating) {
          this.API.failed(this.friend.id, "网络连接已断开").catch(() => {});
          this.$message.error("网络连接已断开，通话中断");
          this.close();
        }
      };
      this.agora.onTokenExpire = () => {
        this.$message.error("通话凭据已过期，通话结束");
        this.API.handup(this.friend.id).catch(() => {});
        this.close();
      };
    },
    // 麦克风权限预检: 采集失败时在发起/接听前就把话说清楚,
    // 而不是等入会后对方发现听不到才排查
    checkMicPermission() {
      return navigator.mediaDevices.getUserMedia({ audio: true }).then((stream) => {
        stream.getTracks().forEach((track) => track.stop());
      });
    },
    micErrorText(e) {
      let name = (e && e.name) || '';
      if (name == 'NotAllowedError' || name == 'SecurityError') {
        return "麦克风权限被拒绝，请在浏览器设置中允许后重试";
      }
      if (name == 'NotFoundError' || name == 'DevicesNotFoundError') {
        return "未找到可用的麦克风设备";
      }
      if (name == 'NotReadableError' || name == 'TrackStartError') {
        return "无法启动麦克风：请确认没有其他应用正在占用";
      }
      return "打开麦克风失败" + (e && e.message ? `：${e.message}` : '');
    },
    // 主叫发起呼叫。只把服务端返回的入会凭据收好, 先不进频道:
    // 声网按"频道内时长×人数"计费, 一发起就入会的话每次响铃都在烧钱,
    // 未接来电本该是零成本 —— 收到接听信令(102)才 join。
    onCall() {
      if (!this.checkDevEnable()) {
        this.close();
        return;
      }
      this.state = "WAITING";
      this.startHeartBeat();
      this.checkMicPermission().then(() => {
        return this.API.call(this.friend.id, 'voice');
      }).then((joinInfo) => {
        this.pendingJoin = joinInfo;
        this.currentCallId = joinInfo && joinInfo.channel;
        // 播放呼叫铃声
        this.audio.play().catch(() => {});
        // 30s 无应答自动取消。真正的判定权在服务端的响铃超时任务上,
        // 这个计时器只是本机 UI 的兜底。
        this.waitTimer = setTimeout(() => {
          this.API.cancel(this.friend.id).catch(() => {});
          this.$message.error("对方无应答");
          this.close();
        }, 30000)
      }).catch((e) => {
        // 麦克风拿不到 / 呼叫接口失败(接口错误信息已由 http 拦截器提示)
        if (e && e.name) {
          this.$message.error(this.micErrorText(e));
        }
        this.close();
      })
    },
    // 被叫接听。accept 接口会把本端的入会信息再返一次, 优先用它
    // (多终端登录时本终端未必收到过呼叫信令), 呼叫信令里的那份做兜底。
    onAccept() {
      if (!this.isWaiting || this.accepting) {
        return;
      }
      this.accepting = true;
      if (!this.checkDevEnable()) {
        this.API.failed(this.friend.id, "对方设备不支持通话").catch(() => {});
        this.close();
        return;
      }
      this.checkMicPermission().then(() => {
        return this.API.accept(this.friend.id);
      }).then((joinInfo) => {
        // 进入房间
        this.waitTimer && clearTimeout(this.waitTimer);
        this.waitTimer = null;
        this.showRoom = true;
        this.state = "CHATING";
        this.audio.pause();
        this.startChatTime();
        return this.joinAndPublish(joinInfo || this.pendingJoin).catch((e) => {
          // 应答已发出但入会失败: 得让对方知道, 别让他对着空频道干等
          console.error('入会失败:', e);
          this.API.failed(this.friend.id, "接听失败").catch(() => {});
          this.$message.error("接入通话失败");
          this.close();
        });
      }).catch((e) => {
        this.accepting = false;
        if (e && e.name) {
          // 麦克风权限问题: 不关弹窗, 用户去浏览器设置放开后还能再点接听
          this.$message.error(this.micErrorText(e));
          return;
        }
        // accept 接口失败(如已在其他设备接听 10005), 错误信息已由拦截器提示
        console.error('接听失败:', e);
        this.close();
      })
    },
    // 主叫得知对方已接听(102 信令 / 页面回前台后的状态对齐都会走到这里),
    // 这才是主叫入频道的时刻, 靠 joining 防重。
    onRemoteAccepted() {
      if (this.joining || this.isChating) {
        return;
      }
      this.waitTimer && clearTimeout(this.waitTimer);
      this.waitTimer = null;
      this.audio.pause();
      // 先把界面推到通话中: 服务端此刻已把这通记成"已接听",
      // 从这里开始计时才和对方看到的时长对得上。入会是紧接着的异步动作。
      this.state = "CHATING";
      this.startChatTime();
      this.joinAndPublish(this.pendingJoin).catch((e) => {
        console.error('入会失败:', e);
        this.API.failed(this.friend.id, "接入通话失败").catch(() => {});
        this.$message.error("接入通话失败");
        this.close();
      });
    },
    joinAndPublish(info) {
      if (!info) {
        return Promise.reject(new Error("未拿到通话凭据"));
      }
      this.joining = true;
      this.initAgora();
      return this.agora.join(info.appId, info.channel, info.token, info.uid).then(() => {
        return this.agora.publishAudio();
      });
    },
    onReject() {
      this.API.reject(this.friend.id).catch(() => {});
      this.close();
    },
    onHandup() {
      this.API.handup(this.friend.id).catch(() => {});
      this.$message.success("您已挂断,通话结束");
      this.close();
    },
    onCancel() {
      this.API.cancel(this.friend.id).catch(() => {});
      this.$message.success("已取消呼叫,通话结束");
      this.close();
    },
    onRTCMessage(msg) {
      let isCall = msg.type == this.$enums.MESSAGE_TYPE.RTC_CALL_VOICE ||
        msg.type == this.$enums.MESSAGE_TYPE.RTC_CALL_VIDEO;
      // 除了发起通话，如果在关闭状态就无需处理
      if (!isCall && this.isClose) {
        return;
      }
      // 信令来源校验: 防止第三方来电污染当前通话、自同步信令串号
      if (!this.acceptSignal(msg, isCall)) {
        return;
      }
      // 迟到信令拦截。acceptSignal 只能分辨"是哪个人", 分辨不了"是哪一通":
      // 挂断后立刻重拨时, 上一通的 106/105 正好在路上, sendId 完全合法,
      // 于是刚建起来的新通话会被上一通的残响关掉。callId(=频道名)带毫秒
      // 时间戳, 每通都不一样, 对不上的直接丢。
      let sig = isCall ? {} : this.parseSignal(msg.content);
      if (sig.callId && this.currentCallId && sig.callId != this.currentCallId) {
        console.log('[RTC] 丢弃过期信令 type=' + msg.type + ' callId=' + sig.callId);
        return;
      }
      // RTC信令处理
      switch (msg.type) {
        case this.$enums.MESSAGE_TYPE.RTC_CALL_VOICE:
        case this.$enums.MESSAGE_TYPE.RTC_CALL_VIDEO:
          // 视频通话已下线, 旧客户端发来的视频呼叫(101)也按语音接
          this.onRTCCall(msg)
          break;
        case this.$enums.MESSAGE_TYPE.RTC_ACCEPT:
          this.onRTCAccept(msg)
          break;
        case this.$enums.MESSAGE_TYPE.RTC_REJECT:
          this.onRTCReject(msg)
          break;
        case this.$enums.MESSAGE_TYPE.RTC_CANCEL:
          this.onRTCCancel(msg, sig)
          break;
        case this.$enums.MESSAGE_TYPE.RTC_FAILED:
          this.onRTCFailed(msg, sig)
          break;
        case this.$enums.MESSAGE_TYPE.RTC_HANDUP:
          this.onRTCHandup(msg)
          break;
        case this.$enums.MESSAGE_TYPE.RTC_CANDIDATE:
          // 声网自己管网络接入, 不需要交换 candidate。
          // 老客户端可能还在发, 直接忽略(信令类型保留, 不动服务端协议)。
          break;
      }
    },
    // 102~106 的 content 服务端统一发 {"callId":"...","reason":"..."} 的 JSON。
    // 解析不出来就当空对象: 旧版服务端发的是空串或裸 reason 字符串,
    // 不能因为解析失败就把信令丢了 —— 那会挂不掉电话。
    parseSignal(content) {
      if (!content) {
        return {};
      }
      try {
        let o = JSON.parse(content);
        return (o && typeof o === 'object') ? o : {};
      } catch (e) {
        return {};
      }
    },
    // 服务端推送的信令只带 sendId/recvId, 不校验来源会串号:
    //   1. 通话中第三方打进来会把界面拉回 WAITING, 接听后 accept 却发给了原对端;
    //   2. handup/reject/cancel/failed 是 sendToSelf 推给自己所有终端的,
    //      不看 recvId 会把本端正在进行的另一通通话一起挂掉;
    //   3. 第三方信令混进当前通话会直接搞坏状态机。
    // 用 != 而不是 !== 是故意的: 后端返回的 id 可能是字符串, 不能按类型比。
    acceptSignal(msg, isCall) {
      if (this.isClose) {
        // 已关闭状态只放行新来电(此时 friend 是上一通的残留, 不能拿来比对);
        // 自己其它终端发起呼叫的回显不能当成来电处理
        return isCall && !msg.selfSend;
      }
      // 自己其它终端的同步信令: 只认针对当前对端的那一条
      if (msg.selfSend) {
        if (isCall) {
          // 通话中自己其它终端发起呼叫的回显, 本端不处理
          console.log('[RTC] 丢弃自己其它终端的呼叫回显 recv=' + msg.recvId);
          return false;
        }
        if (msg.recvId != this.friend.id) {
          console.log('[RTC] 丢弃其它会话的自同步信令 type=' + msg.type + ' recv=' + msg.recvId);
          return false;
        }
        return true;
      }
      if (isCall) {
        if (msg.sendId == this.friend.id) {
          return true;
        }
        // 通话中的第三方来电: 绝不能覆盖当前通话。替用户回一个忙线,
        // 对方立刻知道结果, 而不是干等 30 秒无应答。
        console.log('[RTC] 通话中收到第三方来电, 回复忙线 send=' + msg.sendId);
        this.API.failed(msg.sendId, '对方正忙').catch((e) => console.log('[RTC] 回复忙线失败', e));
        return false;
      }
      if (msg.sendId != this.friend.id) {
        console.log('[RTC] 丢弃第三方信令 type=' + msg.type + ' send=' + msg.sendId);
        return false;
      }
      return true;
    },
    onRTCCall(msg) {
      // 呼叫信令的 content 是服务端给被叫签好的入会信息(appId/channel/uid/token)。
      // 先存下来, 接听时优先用 accept 接口返的那份, 这份做兜底。
      this.pendingJoin = this.parseSignal(msg.content);
      this.currentCallId = this.pendingJoin.channel || null;
      this.isHost = false;
      this.$http({
        url: `/friend/find/${msg.sendId}`,
        method: 'get'
      }).then((friend) => {
        this.friend = friend;
        this.state = "WAITING";
        this.audio.play().catch(() => {});
        this.startHeartBeat();
        // 30s未接听自动挂掉(同主叫侧: 真正的判定在服务端, 这里只是 UI 兜底)
        this.waitTimer = setTimeout(() => {
          this.API.failed(this.friend.id, "对方无应答").catch(() => {});
          this.$message.error("您未接听");
          this.close();
        }, 30000)
      }).catch((e) => {
        console.error('获取好友信息失败:', e);
        this.close();
      })
    },
    onRTCAccept(msg) {
      if (msg.selfSend) {
        // 在其他设备接听
        this.$message.success("已在其他设备接听");
        this.close();
      } else {
        // 对方接了。content 只带 callId, 不再有 SDP —— 主叫此刻才入频道
        this.onRemoteAccepted();
      }
    },
    onRTCReject(msg) {
      if (msg.selfSend) {
        this.$message.success("已在其他设备拒绝");
        this.close();
      } else {
        this.$message.error("对方拒绝了您的通话请求");
        this.close();
      }
    },
    onRTCFailed(msg, sig) {
      // 失败原因现在放在 content JSON 的 reason 里; 旧版服务端发的是裸字符串,
      // 那时 parseSignal 拿不到 reason, 回退到直接用 content。
      let reason = sig.reason ||
        (typeof msg.content === 'string' && msg.content.charAt(0) !== '{' ? msg.content : '') || '通话失败';
      this.$message.error(reason);
      this.close();
    },
    onRTCCancel(msg, sig) {
      if (msg.selfSend) {
        this.$message.success("已在其他设备取消");
      } else if (sig.reason) {
        // 104 还承载了多端同步信号(自己在其他终端接听/拒绝后, 服务端借
        // RTC_CANCEL 通知本终端停止响铃), 靠 reason 区分并原样展示
        this.$message.success(sig.reason);
      } else {
        this.$message.success("对方取消了呼叫");
      }
      this.close();
    },
    onRTCHandup() {
      // 对方挂断
      this.$message.success("对方已挂断");
      this.close();
    },
    // 页面重新可见时与服务端对一次状态: 浏览器标签页被挂起/休眠期间
    // WebSocket 信令可能丢失, 回来时界面可能还停在响铃甚至假通话中。
    // 服务端的 session 是唯一事实。
    onVisibilityChange() {
      if (document.hidden || this.isClose || !this.friend.id) {
        return;
      }
      this.API.status(this.friend.id).then((d) => {
        if (this.isClose || !d) {
          return;
        }
        if (d.state === 'ENDED') {
          this.$message.success("通话已结束");
          this.close();
          return;
        }
        // 服务端上已经是新的一通了(挂起期间挂断又重拨过), 本页这通属于过去
        if (d.callId && this.currentCallId && d.callId != this.currentCallId) {
          this.$message.success("通话已结束");
          this.close();
          return;
        }
        // 服务端说已接通, 本端却还停在等待 —— 挂起期间漏掉了 102。
        // accepting 期间不插手: 那是本机正在走接听流程, 让它自己跑完。
        if (d.state === 'CHATING' && this.isWaiting && !this.accepting) {
          if (d.caller) {
            this.onRemoteAccepted();
          } else {
            // 自己是被叫但本机没接 —— 是另一台设备接起来的
            this.$message.success("已在其他设备接听");
            this.close();
          }
        }
      }).catch(() => {});
    },
    startChatTime() {
      this.videoTime = 0;
      this.videoTimer && clearInterval(this.videoTimer);
      this.videoTimer = setInterval(() => {
        this.videoTime++;
      }, 1000)
    },
    checkDevEnable() {
      if (!this.agora.isEnable()) {
        this.$message.error("初始化通话组件失败，原因可能是: 1.服务器缺少ssl证书 2.通话SDK未能加载");
        return false;
      }
      return true;
    },
    startHeartBeat() {
      // 每15s推送一次心跳, 给服务端的通话 session 续命
      this.heartbeatTimer && clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = setInterval(() => {
        this.API.heartbeat(this.friend.id).catch(() => {});
      }, 15000)
    },
    close() {
      this.showRoom = false;
      this.audio.pause();
      // 释放声网资源(退频道+关麦克风)。不等它完成: 收尾是异步的,
      // 界面状态先复位, 下一通通话用的是全新的客户端实例。
      this.agora.leave();
      this.agora = new AgoraVoiceClient();
      this.videoTime = 0;
      this.videoTimer && clearInterval(this.videoTimer);
      this.heartbeatTimer && clearInterval(this.heartbeatTimer);
      this.waitTimer && clearTimeout(this.waitTimer);
      this.videoTimer = null;
      this.heartbeatTimer = null;
      this.waitTimer = null;
      this.state = 'CLOSE';
      this.pendingJoin = null;
      this.currentCallId = null;
      this.accepting = false;
      this.joining = false;
      this.quitting = false;
    },
    onQuit() {
      if (this.quitting) {
        return;
      }
      this.quitting = true;
      if (this.isChating) {
        this.onHandup()
      } else if (this.isWaiting) {
        this.onCancel();
      } else {
        this.close();
      }
    }
  },
  computed: {
    title() {
      let strTitle = `语音通话-${this.friend.nickName}`;
      if (this.isChating) {
        strTitle += `(${this.currentTime})`;
      } else if (this.isWaiting) {
        strTitle += `(呼叫中)`;
      }
      return strTitle;
    },
    currentTime() {
      let min = Math.floor(this.videoTime / 60);
      let sec = this.videoTime % 60;
      let strTime = min < 10 ? "0" : "";
      strTime += min;
      strTime += ":"
      strTime += sec < 10 ? "0" : "";
      strTime += sec;
      return strTime;
    },
    isChating() {
      return this.state == "CHATING";
    },
    isWaiting() {
      return this.state == "WAITING";
    },
    isClose() {
      return this.state == "CLOSE";
    }
  },
  mounted() {
    // 初始化音频文件
    this.initAudio();
  },
  created() {
    // 监听页面刷新事件
    window.addEventListener('beforeunload', () => {
      this.onQuit();
    });
    // 标签页回前台时与服务端对齐通话状态
    this.onVisibilityChangeBound = () => this.onVisibilityChange();
    document.addEventListener('visibilitychange', this.onVisibilityChangeBound);
  },
  beforeDestroy() {
    document.removeEventListener('visibilitychange', this.onVisibilityChangeBound);
    this.onQuit();
  }
}
</script>

<style lang="scss" scoped>
.rtc-private-video {
  position: relative;

  .el-loading-text {
    color: white !important;
    font-size: 16px !important;
  }

  .path {
    stroke: white !important;
  }

  .rtc-voice-box {
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 300px;
    background-color: var(--im-color-primary-light-9);

    .rtc-voice-name {
      text-align: center;
      font-size: 20px;
      font-weight: 600;
    }
  }

  .rtc-control-bar {
    display: flex;
    justify-content: space-around;
    padding: 10px;

    .icon {
      font-size: 50px;
      cursor: pointer;

      &.is-disabled {
        opacity: 0.5;
        pointer-events: none;
      }
    }
  }
}
</style>
